/**
 * Council Agenda + Packet — public `/meetings` list + `/meetings/{id}` detail (agenda-packet.md §6,
 * §10A Phase 4 / UI spec screens 7-8; list redesigned per meetings-public-relaunch.md — typed
 * Agenda/Packet/Minutes/Recap/Watch doc links + a "Subscribe to updates" CTA, superseding the
 * badge-row). No auth, PII-free (the backend's allowlist projection never carries staff ids/
 * reporter data — meetings.go's file doc). Reuses the FaqPage sanitized-HTML render pattern (♻️
 * `.td-faq-answer`), the `TownHeader`/`ResidentThemeRoot` public shell, and supports `?embed=1`
 * like the other public pages.
 *
 * Locked UI decisions (Lister, §9b):
 *  - flat chronological list, no per-body filter chips (v1 — `body` is just a label).
 *  - "Request official notice" is a `mailto:` affordance (the underlying KOMA notice-request
 *    process is unconfirmed with counsel — no in-product form yet); untouched by the redesign
 *    (meetings-public-relaunch.md §2 — additive, not a replacement).
 *  - a visible "Print this page" button on the packet detail.
 *
 * MONTH-NAV PAST MEETINGS (Lister/LV team, 2026-08-28 — the LV website team's #1 request,
 * REPLACING the year-grouped "Show all past meetings" collapse from 2026-08-23; the archive had
 * grown past what even a per-year view kept scannable): "Past meetings" is no longer sectioned by
 * calendar year — it's one `<section>` per calendar MONTH of `meetingDate` (the ISO date's
 * `slice(0,7)`, e.g. "2026-08" — kept deliberately simple so it matches internal/meetingshtml's Go
 * prerender generator EXACTLY, same grouping key, same monthKeyLabel-equivalent "August 2026"
 * text), months descending, meetings within a month in the server's existing most-recent-first
 * order (no client re-sort). Only ONE month renders at a time — defaulting to the CURRENT calendar
 * month (or the single most-recent month with a meeting, if today's own month has none) — with a
 * Prev/Next `<button>` pair and a `<select>` month/year picker to jump directly. Prev/Next only
 * ever walk the list of months that ACTUALLY have a meeting (never a synthesized calendar), so
 * stepping can't wander into an empty stretch of the archive — the "bounded to the real data range,
 * never a dead end" requirement falls out of there being no other month to reveal. "Upcoming" is
 * UNCHANGED — always rendered in full, pinned above the month nav, unaffected by the selected
 * month. Heading levels: page `<h1>` → "Upcoming"/"Past meetings" `<h2>` (unchanged) → the visible
 * month's `<h3>` nested under "Past meetings" (non-skipping, `aria-labelledby` per section like the
 * existing pattern). Chromeless embed still only suppresses the page `<h1>`/subtitle/Subscribe CTA
 * — the Upcoming/Past/month-nav structure renders the same in every mode. Switching months moves
 * focus to the newly-shown month's heading AND updates an `aria-live` status region (WCAG 2.1 AA
 * 4.1.2 + sensible focus management on what would otherwise be a silent visibility-only DOM
 * change) — mirrors internal/meetingshtml's static twin (meetings-theme.js) EXACTLY, key-for-key,
 * so the two surfaces never drift. `useEmbedResize`'s ResizeObserver (already wired for the page
 * root) picks up the height change on every switch for free — no extra resize plumbing needed here
 * (closes the LV team's "double scrollbar" ask #2: the iframe grows/shrinks to fit the one visible
 * month instead of gaining an inner scroll region).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { listPublicMeetings, getPublicMeeting } from "../api/index.js";
import type { PublicMeetingView, PublicMeetingDetail, PublicMeetingDocLink } from "../api/types.js";
import { TownHeader } from "../components/TownHeader.js";
import { ResidentThemeRoot } from "../components/ResidentThemeRoot.js";
import { Skeleton, SkeletonRegion } from "../components/Skeleton.js";
import { PublicMeetingView as PublicMeetingViewRender, formatTime12 } from "../components/PublicMeetingView.js";
import { useTheme } from "../theme/ThemeProvider.js";
import { useDocumentTitle, pageTitle } from "../components/useDocumentTitle.js";
import { useEmbedClose } from "../components/useEmbedClose.js";
import { useEmbedResize } from "../components/useEmbedResize.js";
import { FileIcon, ClockHistoryIcon, PlayFrameIcon, MailIcon } from "../components/icons.js";

/**
 * INLINE-EMBED CHROMELESS MODE (meetings-embed-launcher dispatch, `?embed=1&chrome=off`, set by
 * apps/widget's `mountMeetingsInline`): suppresses the `<h1>Meetings` title, the subtitle, and the
 * "Subscribe to updates" CTA — TownHeader already renders nothing in any embed (TownHeader.tsx) so
 * chromeless leaves ONLY the meetings list. Also drops the `.td-intake--embed` cream card's outer
 * padding/background via `.td-intake--chromeless` (app.css) so the list sits flush/transparent on
 * the host page instead of in a padded card. Plain `?embed=1` alone (no `chrome=off`) is
 * unchanged — backward compat with the raw iframe embed already documented in
 * docs/runbooks/linnvalley-meetings-embed.md. `useEmbedResize` posts
 * `{type:"towndesk:meetings-resize", height}` to the parent whenever content height changes so the
 * launcher can auto-grow the iframe instead of showing an inner scrollbar (mirrors the ordinance
 * viewer's `towndesk:code-resize`; only emitted when actually embedded).
 */

/** Fixed render order (meetings-public-relaunch.md §1.4) — the server doesn't guarantee this
 *  ordering (it's built from insertion order, first-encountered-per-type), so the client re-sorts
 *  defensively rather than trusting wire order. */
const DOC_LINK_ORDER: PublicMeetingDocLink["type"][] = [
  "agenda",
  "packet",
  "minutes",
  "recap",
  "video",
  "other",
];

function sortedDocLinks(documents: PublicMeetingDocLink[]): PublicMeetingDocLink[] {
  return [...documents].sort(
    (a, b) => DOC_LINK_ORDER.indexOf(a.type) - DOC_LINK_ORDER.indexOf(b.type),
  );
}

/** Decorative icon per doc-link type (meetings-public-relaunch.md §1.2) — Agenda/Packet/Minutes
 *  reuse the existing FileIcon; Recap/Watch get their own dedicated glyphs. */
function DocLinkIcon({ type }: { type: PublicMeetingDocLink["type"] }): JSX.Element {
  if (type === "recap") return <ClockHistoryIcon width={14} height={14} aria-hidden="true" />;
  if (type === "video") return <PlayFrameIcon width={14} height={14} aria-hidden="true" />;
  return <FileIcon width={14} height={14} aria-hidden="true" />;
}

const MONTH_LABEL = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function dateParts(iso: string): { mo: string; dy: string } {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return { mo: "—", dy: "—" };
  return { mo: MONTH_LABEL[m - 1] ?? "—", dy: String(d) };
}

const MONTH_LONG_LABEL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** "YYYY-MM" -> "August 2026" — mirrors internal/meetingshtml's monthKeyLabel exactly (same key
 *  shape, same human label), so the two surfaces' month headings/picker text never drift. */
function monthKeyLabel(key: string): string {
  const [y, m] = key.split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) return "Unknown";
  return `${MONTH_LONG_LABEL[m - 1]} ${y}`;
}

interface MonthGroup {
  key: string; // "YYYY-MM"
  label: string; // "August 2026"
  meetings: PublicMeetingView[];
}

/** Groups past meetings by calendar month of `meetingDate` (the ISO date's `slice(0,7)`, e.g.
 *  "2026-08" — mirrors internal/meetingshtml's groupPastByMonth key-for-key), months descending
 *  (newest first). Meetings within a month keep the server's own most-recent-first order
 *  (listPublicMeetings already sorts past meetings newest-first) — no re-sort here. Only months
 *  that actually have a meeting are included — this IS the "bounded to the real data range" list
 *  the prev/next controls walk, so there is no empty month to ever land on. */
function groupByMonth(meetings: PublicMeetingView[]): MonthGroup[] {
  const byMonth = new Map<string, PublicMeetingView[]>();
  for (const m of meetings) {
    const key = m.meetingDate.slice(0, 7);
    const group = byMonth.get(key);
    if (group) group.push(m);
    else byMonth.set(key, [m]);
  }
  return [...byMonth.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, ms]) => ({ key, label: monthKeyLabel(key), meetings: ms }));
}

/** Resolves the month-nav's initial "YYYY-MM" key: today's own calendar month if it has at least
 *  one past meeting, otherwise the single most-recent month that does (monthGroups is
 *  newest-first) — mirrors internal/meetingshtml's resolveDefaultMonth exactly, so the SPA and the
 *  static prerender always open on the identical default month. A tenant with zero past meetings
 *  still gets today's own month key — there's nothing to fall back to. */
function resolveDefaultMonth(today: string, monthGroups: MonthGroup[]): string {
  const todayKey = today.slice(0, 7);
  if (monthGroups.length === 0) return todayKey;
  if (monthGroups.some((g) => g.key === todayKey)) return todayKey;
  return monthGroups[0]!.key;
}

interface MeetingsPageProps {
  embed?: boolean;
  /** `?embed=1&chrome=off` only — see the file-header note above. No-op when `embed` is false. */
  chromeless?: boolean;
}

export function MeetingsPage({ embed = false, chromeless = false }: MeetingsPageProps): JSX.Element {
  const { id } = useParams<{ id: string }>();
  if (id) return <MeetingDetailScreen embed={embed} id={id} />;
  return <MeetingsListScreen embed={embed} chromeless={embed && chromeless} />;
}

function MeetingsListScreen({
  embed,
  chromeless,
}: {
  embed: boolean;
  chromeless: boolean;
}): JSX.Element {
  const { config } = useTheme();
  useDocumentTitle(pageTitle("Meetings", config.townName));
  useEmbedClose(embed);
  const rootRef = useRef<HTMLDivElement>(null);
  useEmbedResize(embed, rootRef);

  // CHROMELESS embed: `.td-intake--chromeless` makes our container transparent, but the iframe's own
  // <body> still paints the cream `--td-paper` background (base.css) — which reads as a faint
  // off-white panel on the host page. Make the document itself transparent while this surface is
  // mounted chromeless (parity with the /code viewer's embedded body), reverted on unmount.
  useEffect(() => {
    if (!chromeless) return;
    const { documentElement: html, body } = document;
    html.classList.add("td-embed-transparent");
    body.classList.add("td-embed-transparent");
    return () => {
      html.classList.remove("td-embed-transparent");
      body.classList.remove("td-embed-transparent");
    };
  }, [chromeless]);

  const [meetings, setMeetings] = useState<PublicMeetingView[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  // Bumping this re-runs the load effect below — the retry affordance's only job.
  const [reloadKey, setReloadKey] = useState(0);
  // MONTH NAV (file-header note above): the currently-visible "YYYY-MM" past month. null until the
  // meetings load and a default can be resolved (resolveDefaultMonth) — the very first render never
  // has a wrong/guessed month flash before the real default is known.
  const [activeMonth, setActiveMonth] = useState<string | null>(null);
  const monthHeadingRef = useRef<HTMLHeadingElement>(null);
  const [monthStatus, setMonthStatus] = useState("");
  // True only on the render immediately after a user-driven month switch — lets the effect below
  // move focus to the newly-shown month's heading exactly once, never on the initial default-month
  // render (which shouldn't steal focus from wherever the page/crawler landed).
  const userSwitchedMonthRef = useRef(false);

  useEffect(() => {
    if (activeMonth && userSwitchedMonthRef.current) {
      userSwitchedMonthRef.current = false;
      monthHeadingRef.current?.focus();
    }
  }, [activeMonth]);

  useEffect(() => {
    let live = true;
    // Reset to the loading skeleton on every (re)run — including a retry — so a fetch failure never
    // renders a false "No published meetings yet." (a real error mis-shown as genuine-empty).
    setMeetings(null);
    setLoadError(false);
    listPublicMeetings()
      .then((page) => {
        if (!live) return;
        setMeetings(page);
      })
      .catch(() => {
        if (!live) return;
        setLoadError(true);
      });
    return () => {
      live = false;
    };
  }, [reloadKey]);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = (meetings ?? []).filter((m) => m.meetingDate >= today);
  const past = (meetings ?? []).filter((m) => m.meetingDate < today);
  const monthGroups = useMemo(() => groupByMonth(past), [past]);
  const defaultMonth = useMemo(() => resolveDefaultMonth(today, monthGroups), [today, monthGroups]);
  // The resolved default only "wins" until the user picks a month AND that month is still
  // reachable (e.g. a fresh fetch after Retry) — falling back to the freshly-resolved default
  // rather than a stale key that no longer has a group.
  const visibleMonthKey =
    activeMonth && monthGroups.some((g) => g.key === activeMonth) ? activeMonth : defaultMonth;
  const visibleMonthIndex = monthGroups.findIndex((g) => g.key === visibleMonthKey);
  const visibleMonth = visibleMonthIndex >= 0 ? monthGroups[visibleMonthIndex] : undefined;

  function switchMonth(key: string, userDriven: boolean): void {
    if (userDriven) userSwitchedMonthRef.current = true;
    setActiveMonth(key);
    if (userDriven) setMonthStatus(`Showing ${monthKeyLabel(key)}.`);
  }

  return (
    <ResidentThemeRoot
      ref={rootRef}
      className={`td-intake${embed ? " td-intake--embed" : ""}${chromeless ? " td-intake--chromeless" : ""}`}
    >
      {chromeless ? null : (
        <a href="#td-meetings-main" className="td-skip-link">
          Skip to meetings
        </a>
      )}
      <TownHeader embed={embed} subcopy={`Public meetings from ${config.townName}.`} />

      <main id="td-meetings-main" className="td-intake__main">
        {chromeless ? null : (
          <div className="page-head">
            <div>
              <h1 className="td-intake__title">Meetings</h1>
              <p className="page-head__sub">
                Agendas, packets, minutes, and recaps for {config.townName}'s public meetings.
              </p>
            </div>
            <Link
              to={embed ? "/notify/subscribe?embed=1" : "/notify/subscribe"}
              className="td-btn td-btn--primary"
            >
              <MailIcon width={18} height={18} aria-hidden="true" />
              Subscribe to updates
            </Link>
          </div>
        )}

        {loadError ? (
          <div className="td-alert td-alert--danger" role="alert">
            <p>Couldn't load meetings — please try again.</p>
            <button
              type="button"
              className="td-btn td-btn--secondary"
              onClick={() => setReloadKey((k) => k + 1)}
            >
              Retry
            </button>
          </div>
        ) : meetings === null ? (
          <MeetingsListSkeleton />
        ) : meetings.length === 0 ? (
          <p className="td-muted">No published meetings yet.</p>
        ) : (
          <>
            {upcoming.length > 0 ? (
              <section aria-labelledby="mtg-upcoming-h">
                <h2 id="mtg-upcoming-h" className="section-h">
                  Upcoming
                </h2>
                <MeetingRows meetings={upcoming} />
              </section>
            ) : null}
            {monthGroups.length > 0 ? (
              <section aria-labelledby="mtg-past-h">
                <h2 id="mtg-past-h" className="section-h">
                  Past meetings
                </h2>
                <MonthNav
                  monthGroups={monthGroups}
                  visibleMonthIndex={visibleMonthIndex}
                  onSwitch={(key) => switchMonth(key, true)}
                />
                <p className="td-sr-only" aria-live="polite" role="status">
                  {monthStatus}
                </p>
                {visibleMonth ? (
                  <section aria-labelledby={`mtg-month-${visibleMonth.key}-h`}>
                    <h3
                      id={`mtg-month-${visibleMonth.key}-h`}
                      className="section-h section-h--year"
                      // Not natively focusable — tabindex="-1" makes it a valid one-time focus
                      // target on a user-driven month switch (see the effect above), without
                      // adding it to tab order.
                      tabIndex={-1}
                      ref={monthHeadingRef}
                    >
                      {visibleMonth.label}
                    </h3>
                    <MeetingRows meetings={visibleMonth.meetings} />
                  </section>
                ) : null}
              </section>
            ) : null}
          </>
        )}
      </main>

      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
      </footer>
    </ResidentThemeRoot>
  );
}

/** Prev/Next + a labeled month/year `<select>` over `monthGroups` (already newest-first —
 *  index 0 is the newest month, `length - 1` the oldest). Mirrors internal/meetingshtml's static
 *  twin (meetings-theme.js's `#mtg-month-prev`/`#mtg-month-next`/`#mtg-month-picker`) control-for-
 *  control: Prev/Next only ever step within `monthGroups` — the exact list of months that actually
 *  have a meeting — so they're disabled (not merely inert) at the oldest/newest bound rather than
 *  able to wander into an empty stretch of the archive. Real `<button>`s + a real labeled
 *  `<select>` throughout (WCAG 2.1 AA 4.1.2, keyboard-operable by construction). */
function MonthNav({
  monthGroups,
  visibleMonthIndex,
  onSwitch,
}: {
  monthGroups: MonthGroup[];
  visibleMonthIndex: number;
  onSwitch: (key: string) => void;
}): JSX.Element {
  // monthGroups[0] = newest; going "Prev" (chronologically backward) walks to a HIGHER index.
  const canGoPrev = visibleMonthIndex >= 0 && visibleMonthIndex < monthGroups.length - 1;
  const canGoNext = visibleMonthIndex > 0;
  const selectValue = visibleMonthIndex >= 0 ? monthGroups[visibleMonthIndex]!.key : "";

  return (
    <div className="td-monthnav" role="group" aria-label="Browse past meetings by month">
      <button
        type="button"
        className="td-btn td-btn--secondary td-monthnav__btn"
        aria-label="Previous month with meetings"
        disabled={!canGoPrev}
        onClick={() => {
          if (canGoPrev) onSwitch(monthGroups[visibleMonthIndex + 1]!.key);
        }}
      >
        ← Prev
      </button>
      <label className="td-monthnav__pickerlabel">
        <span className="td-sr-only">Jump to month</span>
        <select
          className="td-select td-monthnav__select"
          value={selectValue}
          onChange={(e) => onSwitch(e.target.value)}
        >
          {monthGroups.map((g) => (
            <option key={g.key} value={g.key}>
              {g.label}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className="td-btn td-btn--secondary td-monthnav__btn"
        aria-label="Next month with meetings"
        disabled={!canGoNext}
        onClick={() => {
          if (canGoNext) onSwitch(monthGroups[visibleMonthIndex - 1]!.key);
        }}
      >
        Next →
      </button>
    </div>
  );
}

function MeetingRows({ meetings }: { meetings: PublicMeetingView[] }): JSX.Element {
  return (
    <ul className="td-mtg-list" aria-label="Meetings">
      {meetings.map((m) => {
        const { mo, dy } = dateParts(m.meetingDate);
        const docs = sortedDocLinks(m.documents ?? []);
        return (
          <li key={m.id}>
            {/* The row is a plain <div>, not a <Link> — the title is its own <a> and each doc link
                is its own sibling <a>, so no interactive element is ever nested inside another
                (meetings-public-relaunch.md §1.1/§4 "no nested interactive elements"). */}
            <div className="td-mtg-row">
              <span className="td-mtg-date" aria-hidden="true">
                <span className="mo">{mo}</span>
                <span className="dy">{dy}</span>
              </span>
              <span className="td-mtg-row__main">
                <Link to={`/meetings/${m.id}`} className="td-mtg-row__title-link">
                  {m.body}
                  {m.title ? ` — ${m.title}` : ""}
                </Link>
                <span className="td-mtg-row__sub">
                  {m.startTime ? `${formatTime12(m.startTime)} · ` : ""}
                  {m.location}
                </span>
                <MeetingDocLinks meeting={m} docs={docs} />
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** The typed doc-link row for one meeting (meetings-public-relaunch.md §1.2-§1.4). Renders nothing
 *  (an empty `.td-doclinks`) when the meeting has documents, or the "No documents posted yet" note
 *  when it has none — never both, never an empty flex container with no affordance and no note. */
function MeetingDocLinks({
  meeting,
  docs,
}: {
  meeting: PublicMeetingView;
  docs: PublicMeetingDocLink[];
}): JSX.Element {
  if (docs.length === 0) {
    return <p className="td-no-docs">No documents posted yet — check back closer to the meeting.</p>;
  }
  const { mo, dy } = dateParts(meeting.meetingDate);
  const groupLabel = `Documents for ${meeting.body} — ${mo} ${dy}`;
  return (
    <span className="td-doclinks" role="group" aria-label={groupLabel}>
      {docs.map((d) => (
        <DocLink key={d.type} meeting={meeting} doc={d} mo={mo} dy={dy} />
      ))}
    </span>
  );
}

/** One doc-link pill. Accessible name is the exact spec string (§1.3):
 *  `"{DocLabel} ({format}) — {Mon D} {Body}"`. Watch links open in a new tab and get the
 *  `"(opens in a new tab)"` suffix (external-link honesty, same convention as
 *  AttachmentDownloadRow). Video/Watch has no page-visible "(format)" — its accessible name instead
 *  names the platform ("on YouTube") the way the spec's own Watch example does when the target is a
 *  recognizable host; falls back to no platform name for any other host. An IN-PAGE link
 *  (`doc.isPage` — a published Recap/Minutes ENTITY's deep-link into its own detail page, see
 *  PublicMeetingDocLink's doc comment) renders as a router `Link` (client-side nav, same-tab, no
 *  file to download) with a "(on this page)" accessible-name suffix instead of a fake format
 *  label. */
function DocLink({
  meeting,
  doc,
  mo,
  dy,
}: {
  meeting: PublicMeetingView;
  doc: PublicMeetingDocLink;
  mo: string;
  dy: string;
}): JSX.Element {
  const isVideo = doc.type === "video";
  if (doc.isPage) {
    const accessibleName = `${doc.label} (on this page) — ${mo} ${dy} ${meeting.body}`;
    return (
      <Link className="td-doclink" to={doc.url} aria-label={accessibleName}>
        <DocLinkIcon type={doc.type} />
        {doc.label}
      </Link>
    );
  }
  const format = docFormatLabel(doc.url);
  const host = isVideo ? videoHostLabel(doc.url) : null;
  const accessibleName = isVideo
    ? `${doc.label}${host ? ` on ${host}` : ""} — ${mo} ${dy} ${meeting.body} (opens in a new tab)`
    : `${doc.label} (${format}) — ${mo} ${dy} ${meeting.body}`;
  return (
    <a
      className={`td-doclink${isVideo ? " td-doclink--video" : ""}`}
      href={doc.url}
      aria-label={accessibleName}
      {...(isVideo ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      <DocLinkIcon type={doc.type} />
      {doc.label}
    </a>
  );
}

/** Best-effort file-format label for a doc link's accessible name (§1.3 "(PDF)") — derived from the
 *  URL's extension; falls back to "document" for a non-file (SharePoint folder, etc.) link so the
 *  accessible name never claims a format that isn't there. */
function docFormatLabel(url: string): string {
  const m = /\.([a-z0-9]{2,5})(?:[?#]|$)/i.exec(url);
  if (!m) return "document";
  const ext = m[1]!.toLowerCase();
  if (ext === "pdf") return "PDF";
  if (ext === "doc" || ext === "docx") return "Word";
  return ext.toUpperCase();
}

/** Best-effort platform label for a Watch link's accessible name (§1.3's example: "Video on
 *  YouTube — ..."). Falls back to null (no platform named) for an unrecognized host. */
function videoHostLabel(url: string): string | null {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "");
    if (host.includes("youtube.com") || host === "youtu.be") return "YouTube";
    if (host.includes("vimeo.com")) return "Vimeo";
    if (host.includes("facebook.com")) return "Facebook";
    return null;
  } catch {
    return null;
  }
}

function MeetingDetailScreen({ embed, id }: { embed: boolean; id: string }): JSX.Element {
  const { config } = useTheme();
  const [state, setState] = useState<"loading" | "found" | "notfound">("loading");
  const [detail, setDetail] = useState<PublicMeetingDetail | null>(null);
  useDocumentTitle(
    pageTitle(
      detail ? `${detail.meeting.body}${detail.meeting.title ? ` — ${detail.meeting.title}` : ""}` : "Meeting",
      config.townName,
    ),
  );
  useEmbedClose(embed);

  useEffect(() => {
    setState("loading");
    getPublicMeeting(id).then((d) => {
      setDetail(d);
      setState(d ? "found" : "notfound");
    });
  }, [id]);

  return (
    <ResidentThemeRoot className={`td-intake${embed ? " td-intake--embed" : ""}`}>
      <a href="#td-meeting-main" className="td-skip-link">
        Skip to the meeting packet
      </a>
      <TownHeader embed={embed} subcopy={`Public meetings from ${config.townName}.`} />

      <main id="td-meeting-main" className="td-intake__main">
        {state === "loading" ? (
          <MeetingDetailSkeleton />
        ) : state === "notfound" || !detail ? (
          <>
            <h1 className="td-intake__title">Meeting not found</h1>
            <p className="td-track__sub">This meeting isn't published, or the link is out of date.</p>
            <Link to="/meetings" className="td-btn td-btn--secondary">
              Browse meetings
            </Link>
          </>
        ) : (
          <>
            <Link to="/meetings" className="td-btn td-btn--ghost" style={{ marginBottom: "var(--td-space-4)" }}>
              ← All meetings
            </Link>

            <PublicMeetingViewRender detail={detail} townName={config.townName} />
          </>
        )}
      </main>

      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
      </footer>
    </ResidentThemeRoot>
  );
}

function MeetingsListSkeleton(): JSX.Element {
  return (
    <SkeletonRegion label="Loading meetings…" className="td-skel-rows">
      {Array.from({ length: 3 }).map((_, i) => (
        <div className="td-skel-row" key={i}>
          <Skeleton width="56px" height="56px" />
          <div className="td-skel-row__main">
            <Skeleton width="55%" />
            <Skeleton width="30%" height="var(--td-text-sm)" />
          </div>
        </div>
      ))}
    </SkeletonRegion>
  );
}

function MeetingDetailSkeleton(): JSX.Element {
  return (
    <SkeletonRegion label="Loading meeting…" className="td-skel-rows">
      <Skeleton width="60%" height="var(--td-text-2xl)" />
      <Skeleton width="100%" height="8rem" />
      <Skeleton width="100%" height="12rem" />
    </SkeletonRegion>
  );
}
